import React from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Button, Typography, Box, Avatar,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';

export default function ConfirmDialog({ 
  open, title, message, onConfirm, onCancel, 
  confirmLabel, confirmColor = 'error', confirmIcon = 'delete'
}) {
  const isError = confirmColor === 'error';
  const isWarning = confirmColor === 'warning';
  const isSuccess = confirmColor === 'success';

  const themeColor = isSuccess ? '#10B981' : isWarning ? '#F59E0B' : '#EF4444';
  const themeColorDark = isSuccess ? '#059669' : isWarning ? '#D97706' : '#DC2626';
  const themeBg = isSuccess ? '#ECFDF5' : isWarning ? '#FFFBEB' : '#FEF2F2';

  let HeaderIcon = WarningAmberIcon;
  if (confirmIcon === 'success') HeaderIcon = CheckCircleOutlineIcon;
  if (confirmIcon === 'block') HeaderIcon = BlockIcon;

  let ButtonIcon = DeleteOutlineIcon;
  if (confirmIcon === 'success') ButtonIcon = CheckCircleOutlineIcon;
  if (confirmIcon === 'block') ButtonIcon = BlockIcon;
  if (confirmIcon === 'none') ButtonIcon = null;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Top accent */}
        <Box sx={{
          height: 4,
          background: `linear-gradient(90deg,${themeColor},${themeColorDark})`,
        }} />

        <Box sx={{ px: 3, pt: 3, pb: 2, textAlign: 'center' }}>
          <Avatar sx={{
            width: 56, height: 56, mx: 'auto', mb: 2,
            bgcolor: themeBg,
          }}>
            <HeaderIcon sx={{ fontSize: 28, color: themeColor }} />
          </Avatar>

          <Typography fontWeight={800} fontSize={18} color="text.primary" mb={1}>
            {title || 'Delete Record?'}
          </Typography>
          <Typography fontSize={13.5} color="text.secondary" lineHeight={1.6}>
            {message || 'Are you sure you want to delete this record? This action cannot be undone.'}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 3, pb: 3, pt: 0,
        display: 'flex', flexDirection: 'column', gap: 1,
      }}>
        <Button
          onClick={onConfirm}
          fullWidth
          variant="contained"
          startIcon={ButtonIcon ? <ButtonIcon /> : undefined}
          sx={{
            py: 1.2, fontWeight: 700, fontSize: 14,
            bgcolor: themeColor, borderRadius: '10px',
            boxShadow: `0 4px 14px ${themeColor}55`,
            '&:hover': { bgcolor: themeColorDark, boxShadow: `0 6px 18px ${themeColor}77` },
          }}
        >
          {confirmLabel || 'Yes, Delete'}
        </Button>
        <Button
          onClick={onCancel}
          fullWidth
          variant="outlined"
          sx={{
            py: 1.2, fontWeight: 600, fontSize: 14,
            borderColor: '#E2E8F0', color: '#64748B', borderRadius: '10px',
            '&:hover': { bgcolor: '#F8FAFC', borderColor: '#94A3B8' },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
